import moment from "moment";

/**
 * costBreakdownHelpers - Utility functions for cost breakdown operations
 * 
 * SUMMARY:
 * Contains helper functions for stage hierarchy building, time formatting,
 * stage path generation, and task finding utilities used in cost breakdown.
 * 
 * MODIFICATIONS:
 * - Extracted from cost-breakdown.jsx to improve separation of concerns
 * - Functions remain pure and reusable across the cost breakdown feature
 */

/**
 * Builds the stage hierarchy path for a given task stage ID
 * 
 * Flattens the nested stages structure and finds the path from root to the target stage.
 * Handles both direct path finding and recursive path building for incomplete hierarchies.
 * 
 * @param {Array} stages - Array of stage objects (may be nested)
 * @param {string} taskStageId - The stage ID to find the path for
 * @returns {Array} Array of stage objects representing the hierarchy path [{ id, name }, ...]
 */
export const buildStageHierarchy = (stages, taskStageId) => {
  if (!stages || !taskStageId) return [];

  // Flatten the stages structure to make it easier to traverse
  const flattenStages = (stagesArray, result = [], parentPath = []) => {
    if (!stagesArray || !Array.isArray(stagesArray)) return result;

    stagesArray.forEach(stage => {
      const currentPath = [...parentPath, { id: stage.id, name: stage.name }];
      result.push({
        id: stage.id,
        name: stage.name,
        parentStageId: stage.parent_stage_id,
        path: currentPath
      });

      // Recursively process child stages if any
      if (stage.stages && Array.isArray(stage.stages)) {
        flattenStages(stage.stages, result, currentPath);
      }
    });

    return result;
  };

  const flatStages = flattenStages(stages);

  // Find the stage that matches our task's stage_id
  const taskStage = flatStages.find(s => s.id === taskStageId);
  if (taskStage) {
    return taskStage.path;
  }

  // If we couldn't find a direct path, we need to manually build it
  // This might happen if the stages hierarchy in the data is incomplete
  const buildPathRecursively = (stageId) => {
    if (!stageId) return [];

    const stage = flatStages.find(s => s.id === stageId);
    if (!stage) return [];

    if (!stage.parentStageId) {
      return [{ id: stage.id, name: stage.name }];
    }

    return [...buildPathRecursively(stage.parentStageId), { id: stage.id, name: stage.name }];
  };

  return buildPathRecursively(taskStageId);
};

/**
 * Formats hours between two timestamps
 * 
 * Calculates the duration between start and end times and returns as hours with 2 decimal places.
 * 
 * @param {string|Date} startTime - Start timestamp
 * @param {string|Date} endTime - End timestamp
 * @returns {string} Formatted hours as string (e.g., "8.50") or "N/A" if invalid
 */
export const formatHours = (startTime, endTime) => {
  if (!startTime || !endTime) return "N/A";
  const start = moment(startTime);
  const end = moment(endTime);
  const duration = moment.duration(end.diff(start));
  return duration.asHours().toFixed(2);
};

/**
 * Generates the stage path display string for breadcrumbs and headers
 * 
 * Creates a formatted string showing the stage hierarchy path.
 * For stage breakdowns: shows stage path only
 * For task breakdowns: shows stage path + task name
 * 
 * @param {Array} stageHierarchy - Array of stage objects from buildStageHierarchy
 * @param {Object|null} task - Task object (for task-level breakdowns)
 * @param {Object|null} stage - Stage object (for stage-level breakdowns)
 * @param {boolean} isStageBreakdown - Whether this is a stage-level breakdown
 * @returns {string} Formatted stage path string (e.g., "Stage 1 \\ Stage 2 \\ Task Name")
 */
export const getStagePath = (stageHierarchy, task, stage, isStageBreakdown) => {
  if (isStageBreakdown && stage) {
    if (stageHierarchy.length === 0) return stage.name || 'Stage';
    const stagePath = stageHierarchy.map(s => s.name).join(' \\ ');
    return stagePath;
  }

  if (stageHierarchy.length === 0) return task?.name || 'Task';
  const stagePath = stageHierarchy.map(s => s.name).join(' \\ ');
  return `${stagePath} \\ ${task?.name || 'Task'}`;
};

/**
 * Finds a task within nested stages structure
 * 
 * Recursively searches through nested stages to find a task by ID.
 * Used for determining task-stage relationships in cost attribution.
 * 
 * @param {Array} stages - Array of stage objects (may be nested)
 * @param {string} taskId - The task ID to find
 * @returns {Object|null} Found task object or null if not found
 */
export const findTaskInStages = (stages, taskId) => {
  if (!stages || !Array.isArray(stages) || !taskId) return null;

  const searchStages = (stageArray) => {
    for (const stageNode of stageArray) {
      // Check tasks in current stage
      if (Array.isArray(stageNode.tasks)) {
        const foundTask = stageNode.tasks.find(t => t.id === taskId);
        if (foundTask) return foundTask;
      }
      
      // Recursively search child stages
      if (Array.isArray(stageNode.stages)) {
        const found = searchStages(stageNode.stages);
        if (found) return found;
      }
    }
    return null;
  };

  return searchStages(stages);
};

/**
 * Finds a stage within nested stages structure
 * 
 * Recursively searches through nested stages to find a stage by ID.
 * Used for stage-level cost breakdowns.
 * 
 * @param {Array} stages - Array of stage objects (may be nested)
 * @param {string} stageId - The stage ID to find
 * @returns {Object|null} Found stage object or null if not found
 */
export const findStageInStages = (stages, stageId) => {
  if (!stages || !Array.isArray(stages) || !stageId) return null;

  for (const stageNode of stages) {
    if (stageNode.id === stageId) return stageNode;
    if (Array.isArray(stageNode.stages)) {
      const found = findStageInStages(stageNode.stages, stageId);
      if (found) return found;
    }
  }
  return null;
};
